import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createOrg, listMyOrgs, listMembers, inviteMember, removeMember } from '../api/orgs';
import { authStore } from '../api/client';
import { formatDateTime, roleColor } from '../utils/format';
import { formatRole } from '../utils/formatters';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Badge({ label, colorClass }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold ${colorClass}`}>
      {label}
    </span>
  );
}

/** Auto-dismissing success toast - visible for ~3 s. */
function Toast({ message, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-50 bg-white border border-emerald-200
                 text-emerald-900 text-sm font-medium px-5 py-3 rounded-xl shadow-lg
                 flex items-center gap-2 animate-fade-in"
    >
      <span className="text-emerald-600" aria-hidden="true">&#10003;</span>
      {message}
    </div>
  );
}

/** Skeleton card loader. */
function CardSkeleton() {
  return (
    <div className="animate-pulse space-y-3 p-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-28 bg-slate-200 rounded-xl" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MembersPanel (accordion content for a single org card)
// ---------------------------------------------------------------------------

function MembersPanel({ orgId, currentUserRole, onError }) {
  const [members, setMembers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole]   = useState('MEMBER');
  const [inviting, setInviting]       = useState(false);
  const [inviteMsg, setInviteMsg]     = useState('');
  const hasFetchedRef = useRef(false);

  // Lazy load members on first mount
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listMembers(orgId);
      const list = Array.isArray(res.data) ? res.data : (res.data?.members ?? []);
      console.log('[OrgPage] loaded', list.length, 'members for org', orgId);
      setMembers(list);
    } catch (err) {
      console.error('[OrgPage] listMembers failed:', err.message);
      onError('Failed to load members.');
    } finally {
      setLoading(false);
    }
  }, [orgId, onError]);

  const handleRemove = useCallback(async (email) => {
    if (!window.confirm(`Remove ${email} from this organization?`)) return;
    try {
      await removeMember(orgId, email);
      console.log('[OrgPage] removed member:', email);
      fetchMembers();
    } catch (err) {
      console.error('[OrgPage] removeMember failed:', err.message);
      onError(err.message || 'Failed to remove member.');
    }
  }, [orgId, fetchMembers, onError]);

  const handleInvite = useCallback(async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteMsg('');
    try {
      await inviteMember(orgId, { memberEmail: inviteEmail.trim(), role: inviteRole });
      console.log('[OrgPage] invited', inviteEmail.trim(), 'as', inviteRole);
      setInviteMsg('Invite sent \u2713');
      setInviteEmail('');
      fetchMembers();
    } catch (err) {
      console.error('[OrgPage] inviteMember failed:', err.message);
      setInviteMsg(err.message || 'Failed to send invite.');
    } finally {
      setInviting(false);
    }
  }, [orgId, inviteEmail, inviteRole, fetchMembers]);

  const canManage = currentUserRole === 'OWNER' || currentUserRole === 'ADMIN';

  if (loading) {
    return (
      <div className="p-4 animate-pulse space-y-2">
        {[1, 2].map((i) => <div key={i} className="h-8 bg-slate-200 rounded-lg" />)}
      </div>
    );
  }

  return (
    <div className="border-t border-slate-100 bg-slate-50/80 px-5 py-4 space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <th className="py-2 pr-4">Email</th>
              <th className="py-2 pr-4">Role</th>
              <th className="py-2 pr-4">Invited At</th>
              <th className="py-2 pr-4">Accepted</th>
              {canManage && <th className="py-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.email || m.memberEmail} className="border-b border-slate-100">
                <td className="py-2.5 pr-4 text-slate-800">{m.email || m.memberEmail}</td>
                <td className="py-2.5 pr-4">
                  <Badge label={formatRole(m.role)} colorClass={roleColor(m.role)} />
                </td>
                <td className="py-2.5 pr-4 text-slate-500 text-xs">{formatDateTime(m.invitedAt || m.createdAt)}</td>
                <td className="py-2.5 pr-4">
                  {m.accepted || m.acceptedAt ? (
                    <span className="text-emerald-600 font-medium">✓</span>
                  ) : (
                    <span className="text-slate-400 italic">pending</span>
                  )}
                </td>
                {canManage && (
                  <td className="py-2.5">
                    {(m.role !== 'OWNER') && (
                      <button
                        type="button"
                        onClick={() => handleRemove(m.email || m.memberEmail)}
                        className="text-xs text-red-600 hover:text-red-700 transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {members.length === 0 && (
              <tr>
                <td colSpan={canManage ? 5 : 4} className="py-4 text-center text-slate-500 italic">
                  No members yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canManage && (
        <form onSubmit={handleInvite} className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-600 mb-1">Invite member</label>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="email@example.com"
              required
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg
                         text-sm text-slate-900 placeholder:text-slate-400 outline-none
                         focus:ring-2 focus:ring-teal-500/25 focus:border-teal-400 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Role</label>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="px-3 py-2 bg-white border border-slate-200 rounded-lg
                         text-sm text-slate-900 outline-none focus:ring-2 focus:ring-teal-500/25 focus:border-teal-400"
            >
              <option value="MEMBER">MEMBER</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={inviting}
            className="btn btn-sm btn-primary px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {inviting ? 'Sending...' : 'Send invite'}
          </button>
          {inviteMsg && (
            <span className={`text-xs ${inviteMsg.includes('\u2713') ? 'text-emerald-600' : 'text-red-600'}`}>
              {inviteMsg}
            </span>
          )}
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OrgPage
// ---------------------------------------------------------------------------

/**
 * OrgPage
 *
 * Route: /orgs
 *
 * State:
 *   orgs             - array of organizations the user belongs to
 *   loading          - true while listMyOrgs is in-flight
 *   error            - error message string
 *   hasFetched       - whether at least one successful load has occurred
 *
 *   formOpen         - whether "New Organization" form is visible
 *   formName         - controlled name field
 *   formSlug         - controlled slug field
 *   formLoading      - true while createOrg is in-flight
 *   formError        - inline form error
 *
 *   expandedOrgId    - which org card's members panel is open (accordion - one at a time)
 *   toast            - success toast message (null = hidden)
 *
 * API calls:
 *   listMyOrgs()                - on mount: the signed-in user's memberships (every org for SUPER_ADMIN)
 *   createOrg({ name, slug })   - on form submit
 *   listMembers(orgId)          - lazy on "Manage" click (first open)
 *   inviteMember(orgId, data)   - on invite form submit inside members panel
 *   removeMember(orgId, email)  - on "Remove" click inside members panel
 */
export default function OrgPage() {
  const navigate = useNavigate();

  // -- Org list --
  // The signed-in user is already known: their organizations load straight away.
  // GET /api/orgs with no email returns the CALLER's memberships (server-side, from the
  // JWT) - or every org for a platform admin. No email box, no way to look up someone else.
  const me = authStore.user || {};
  const isPlatformAdmin = me.role === 'SUPER_ADMIN';
  const [orgs, setOrgs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [hasFetched, setHasFetched]   = useState(false);

  // -- Create form --
  const [formOpen, setFormOpen]       = useState(false);
  const [formName, setFormName]       = useState('');
  const [formSlug, setFormSlug]       = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError]     = useState('');

  // -- Accordion --
  const [expandedOrgId, setExpandedOrgId] = useState(null);

  // -- Toast --
  const [toast, setToast] = useState(null);

  // ---------------------------------------------------------------------------
  // Load orgs (the signed-in user's, automatically)
  // ---------------------------------------------------------------------------

  const loadOrgs = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await listMyOrgs();
      const list = Array.isArray(res.data) ? res.data : (res.data?.content ?? res.data?.organizations ?? []);
      setOrgs(list);
      setHasFetched(true);
      // A single organization opens its members straight away.
      if (list.length === 1) setExpandedOrgId(list[0].id || list[0].orgId);
    } catch (err) {
      console.error('[OrgPage] listMyOrgs failed:', err.message);
      setError(err.response?.data?.message || err.message || 'Failed to load organizations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  // ---------------------------------------------------------------------------
  // Create org
  // ---------------------------------------------------------------------------

  const generateSlug = useCallback((name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }, []);

  const handleNameChange = useCallback((value) => {
    setFormName(value);
    setFormSlug(generateSlug(value));
  }, [generateSlug]);

  const handleCreateOrg = useCallback(async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formName.trim()) { setFormError('Organization name is required.'); return; }
    if (!formSlug.trim()) { setFormError('Slug is required.'); return; }

    setFormLoading(true);
    console.log('[OrgPage] creating org:', formName, formSlug);

    try {
      await createOrg({ name: formName.trim(), slug: formSlug.trim() });
      console.log('[OrgPage] org created successfully');
      setFormName('');
      setFormSlug('');
      setFormOpen(false);
      setToast('Organization created successfully!');
      // Refresh list (the creator is the new org's OWNER)
      loadOrgs();
    } catch (err) {
      console.error('[OrgPage] createOrg failed:', err.message);
      const errorCode = err.response?.data?.errorCode || err.response?.data?.code;
      if (errorCode === 'SLUG_TAKEN') {
        setFormError('This slug is already taken. Please choose a different one.');
      } else {
        setFormError(err.message || 'Failed to create organization.');
      }
    } finally {
      setFormLoading(false);
    }
  }, [formName, formSlug, loadOrgs]);

  // ---------------------------------------------------------------------------
  // Accordion toggle
  // ---------------------------------------------------------------------------

  const toggleAccordion = useCallback((orgId) => {
    setExpandedOrgId((prev) => prev === orgId ? null : orgId);
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto w-full space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">Organization</p>
          <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Teams</h1>
          <p className="text-sm text-ink-faint mt-0.5">The organizations you belong to, with their members and invites.</p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen((p) => !p)}
          className={`btn ${formOpen ? 'btn-secondary' : 'btn-primary'} self-start sm:self-auto`}
        >
          {formOpen ? 'Cancel' : '+ New organization'}
        </button>
      </div>

      {/* ── Main content ────────────────────────────────────────────────── */}
      <div className="space-y-8">

        {/* ── Create org form (collapsible) ─────────────────────────────── */}
        {formOpen && (
          <section className="card p-6">
            <h2 className="font-heading text-base font-semibold text-ink mb-4">Create organization</h2>
            <form onSubmit={handleCreateOrg} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Organization name</label>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Acme Corp"
                    required
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Slug</label>
                  <input
                    type="text"
                    value={formSlug}
                    onChange={(e) => setFormSlug(e.target.value)}
                    placeholder="acme-corp"
                    required
                    className="input font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">URL-friendly identifier (auto-generated from name)</p>
                </div>
              </div>
              {formError && (
                <p className="text-sm text-red-600">{formError}</p>
              )}
              <button
                type="submit"
                disabled={formLoading}
                className="btn btn-primary disabled:opacity-50"
              >
                {formLoading ? 'Creating...' : 'Create organization'}
              </button>
            </form>
          </section>
        )}

        {/* ── Error state ───────────────────────────────────────────────── */}
        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800">
            {error}
          </div>
        )}

        {/* ── Loading state ─────────────────────────────────────────────── */}
        {loading && <CardSkeleton />}

        {/* ── Orgs list (card grid) ─────────────────────────────────────── */}
        {hasFetched && !loading && orgs.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink">
                {isPlatformAdmin ? 'All organizations (platform admin)' : 'Your organizations'}
                <span className="ml-2 text-xs font-normal text-ink-faint">
                  {orgs.length} total{me.email ? ` · signed in as ${me.email}` : ''}
                </span>
              </h2>
              <button
                type="button"
                onClick={() => loadOrgs()}
                className="text-xs font-medium text-teal-600 hover:text-teal-700 transition-colors"
              >
                Refresh
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {orgs.map((org) => {
                const orgId = org.id || org.orgId;
                const isExpanded = expandedOrgId === orgId;

                return (
                  <div
                    key={orgId}
                    className="card overflow-hidden transition-all duration-200"
                  >
                    {/* Card header */}
                    <div className="p-5 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 shrink-0 bg-teal-50 border border-teal-100 rounded-lg
                                        flex items-center justify-center text-lg text-teal-700">
                          🏢
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-heading text-base font-semibold text-ink truncate">{org.name}</h3>
                            {org.myRole
                              ? <Badge label={`You: ${formatRole(org.myRole)}`} colorClass={roleColor(org.myRole)} />
                              : isPlatformAdmin && <Badge label="Not a member" colorClass="bg-slate-100 text-slate-600" />}
                          </div>
                          <p className="text-xs font-mono text-ink-faint mt-0.5">@{org.slug}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {(org.memberCount !== undefined || org.membersCount !== undefined) && (
                          <span className="text-xs font-mono text-ink-faint hidden sm:inline">
                            {org.memberCount ?? org.membersCount} member{(org.memberCount ?? org.membersCount) !== 1 ? 's' : ''}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleAccordion(orgId)}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors
                            ${isExpanded
                              ? 'bg-teal-50 border-teal-200 text-teal-900'
                              : 'border-line text-ink-soft hover:bg-canvas hover:border-line'
                            }`}
                        >
                          {isExpanded ? 'Close' : 'Manage'}
                        </button>
                      </div>
                    </div>

                    {/* Members accordion panel */}
                    {isExpanded && (
                      <MembersPanel
                        orgId={orgId}
                        currentUserRole={isPlatformAdmin ? 'OWNER' : (org.myRole || 'MEMBER')}
                        onError={(msg) => setError(msg)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Empty state ───────────────────────────────────────────────── */}
        {hasFetched && !loading && orgs.length === 0 && (
          <div className="card p-10 text-center">
            <p className="text-3xl mb-3" aria-hidden="true">🏢</p>
            <p className="text-sm text-slate-600">
              You&apos;re not part of any organization yet. Create one to get started.
            </p>
          </div>
        )}

      </div>

      {/* ── Toast ───────────────────────────────────────────────────────── */}
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
